import { useEffect } from "react";
import type { AppInfo } from "../../data/apps";

const SITE = "https://www.zinnobyte.com";

interface SeoProps {
  title: string;
  description: string;
  /** Path starting with /, e.g. /apps/guffaw */
  path: string;
  /** Absolute or /public path to a preview image */
  image?: string;
  /** Pass the app to emit App Store structured data (rich results) */
  app?: AppInfo;
}

/** Sets document head tags on mount / route change. Lightweight alternative
 *  to react-helmet for a small site. */
export default function Seo({ title, description, path, image, app }: SeoProps) {
  useEffect(() => {
    document.title = title;

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", `${SITE}${path}`);
    if (image) {
      const abs = image.startsWith("http") ? image : `${SITE}${image}`;
      setMeta("property", "og:image", abs);
      setMeta("name", "twitter:card", "summary_large_image");
      setMeta("name", "twitter:image", abs);
    }
    setLink("canonical", `${SITE}${path}`);

    // Apple Smart App Banner — iOS Safari shows a native "open in App Store"
    // strip at the top of the page for this app.
    if (app?.appStoreUrl) {
      const idMatch = app.appStoreUrl.match(/id(\d+)/);
      if (idMatch) setMeta("name", "apple-itunes-app", `app-id=${idMatch[1]}`);
    }

    // JSON-LD structured data → eligible for rich results in Google
    // (star rating + price shown directly in search listings).
    const ldId = "seo-jsonld";
    document.getElementById(ldId)?.remove();
    if (app) {
      const ld: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "MobileApplication",
        name: `${app.name} — ${app.subtitle}`,
        operatingSystem: "iOS",
        applicationCategory: categoryToSchema(app.category),
        description: app.tagline,
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        ...(app.appStoreUrl ? { installUrl: app.appStoreUrl } : {}),
        ...(app.icon ? { image: `${SITE}${app.icon}` } : {}),
      };
      if (app.rating && app.ratingCount) {
        ld.aggregateRating = {
          "@type": "AggregateRating",
          ratingValue: app.rating,
          ratingCount: parseInt(app.ratingCount.replace(/\D/g, "")) * 1000 || 1,
        };
      }
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = ldId;
      script.textContent = JSON.stringify(ld);
      document.head.appendChild(script);
    }

    return () => {
      // Remove app-specific tags when navigating away
      document.getElementById(ldId)?.remove();
      document.querySelector('meta[name="apple-itunes-app"]')?.remove();
    };
  }, [title, description, path, image, app]);

  return null;
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function categoryToSchema(category: string): string {
  const map: Record<string, string> = {
    "Photo & Video": "MultimediaApplication",
    "Health & Fitness": "HealthApplication",
    Entertainment: "EntertainmentApplication",
    Utilities: "UtilitiesApplication",
    Education: "EducationalApplication",
  };
  return map[category] ?? "MobileApplication";
}
